/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode } from "react"
import { Tooltip } from "@mui/material"
import {
  HeartSiteCode,
  isHeartSiteCode,
  PcgExamPreviewEvidence,
  PcgReviewRecordingBundle,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * APTM Exam Overview — the anatomical body-map block of the exam workspace
 * sidebar (Medaica Viewer v0.9.1 `.aptm-preview`): the torso map with one
 * marker per auscultation site (A/P/T/M letters at the handoff's landmark
 * percentages) and the composed hover rationale. Styling comes from
 * pcg-exam-workspace.css; the map image is the HCP public asset, drawn as a
 * decorative <img> behind the markers.
 *
 * Anatomy comes from each recording's server-derived site. The map can
 * only draw the four heart sites, so the component renders NOTHING when any
 * recording lies elsewhere (lung points, `other`, unlabeled); those exams
 * keep the generic recording rail as the navigation surface (see
 * aptmLayoutFits).
 *
 * Recordings are grouped per site, as upstream: one marker per site. Each
 * marker shows ONE recording of its site — the selected one when the site is
 * selected, else the site's first — and its state is that recording's alone:
 * the fill is its evidence (`usable` when it has Timing evidence or an
 * energy-ready review range, `limited` otherwise; the current site is white),
 * the ring is its review decision (`accepted` green, `rejected` red), and a
 * site with several recordings carries an "n/N" index naming the displayed
 * one. A click opens the site's first recording; when one of the site is
 * already selected, each further click advances to the next recording at
 * that site and wraps (P1 → P2 → P1, as handoff v0.8.3.17). The exam-wide
 * arrows and dots are unaffected.
 *
 * The evidence flags come from the service's preview evidence (`timing`,
 * and `energy`, with the older `soi` flag standing in for servers that
 * predate it). Upstream v0.9 derives them from its engine's evaluation
 * range, which is not part of this platform.
 *
 * Deviations from the handoff: marker clicks select in-page instead of full
 * page navigation; MUI Tooltip carries the composed hover text, shown only
 * while the viewer's Tips On / Off preference is on (upstream's markers take
 * part in that switch through its smart hover); the upstream dead "Summary"
 * line is not ported. `children` (the Patient Context strip) render after
 * the map, inside the block, as upstream.
 */

// Handoff marker letters and landmark positions (percent of the 266x196 map)
const MARKERS: { [code in HeartSiteCode]: { letter: string; left: number; top: number } } = {
  aortic: { letter: "A", left: 40, top: 31.5 },
  pulmonic: { letter: "P", left: 60.5, top: 31.5 },
  tricuspid: { letter: "T", left: 62, top: 54.5 },
  mitral: { letter: "M", left: 70, top: 70 },
}

const MAP_IMAGE_URL = "/aptm-body-map-anatomical-v5.jpg"

/** True when every recording sits at one of the four heart sites the map draws. */
const aptmLayoutFits = (recordings: { site: { code: string } | null }[]): boolean =>
  recordings.length > 0 && recordings.every((recording) => isHeartSiteCode(recording.site?.code))

/**
 * A marker's hover text (upstream `hover`): the site, the recording count,
 * whether the displayed recording is a useful review source and on what
 * evidence, which evidence is available, and how the click navigates. The
 * useful-source reasons are named alphabetically, as upstream sorts them.
 */
const hoverText = (label: string, count: number, hasTiming: boolean, hasEnergy: boolean): string => {
  const usefulReasons = [hasEnergy ? "energy-ready range" : "", hasTiming ? "timing evidence" : ""].filter(Boolean)
  let evidenceHover = "No useful Timing evidence or energy-ready range surfaced."
  if (hasTiming && hasEnergy) {
    evidenceHover = "Timing evidence and an energy-ready review range are available."
  } else if (hasTiming) {
    evidenceHover = "Timing evidence available; energy-ready range limited or unavailable."
  } else if (hasEnergy) {
    evidenceHover = "Energy-ready review range available; Timing evidence limited or unavailable."
  }
  const recommendationHover =
    usefulReasons.length > 0
      ? `Useful review source: ${usefulReasons.join(" and ")}.`
      : "Limited review source: useful preview confidence is insufficient."
  const countHover = count > 1 ? `${count} recordings at this site. ` : ""
  const navigationHover =
    count > 1
      ? `Click repeatedly to cycle through the ${count} ${label} recordings.`
      : `Click to open the ${label} recording.`
  return `${label}. ${countHover}${recommendationHover} ${evidenceHover} ${navigationHover}`
}

/**
 * The displayed recording's review state as the marker's hover and label
 * name it (upstream `reviewState`): the decision when there is one, else
 * the evidence state.
 */
const reviewStateText = (decision: string | undefined, useful: boolean): string => {
  if (decision === "accepted") {
    return "Accepted for Report"
  }
  if (decision === "rejected") {
    return "Rejected"
  }
  return useful ? "Useful evidence" : "Limited evidence"
}

const ExamOverviewMap = ({
  recordings,
  previewEvidence,
  selectedAuscultationId,
  onSelect,
  tipsEnabled = true,
  children,
}: {
  recordings: PcgReviewRecordingBundle[]
  previewEvidence: PcgExamPreviewEvidence | null
  selectedAuscultationId: string | null
  onSelect: (auscultationId: string) => void
  /** The viewer's Tips On / Off preference; off hides the markers' hover text. */
  tipsEnabled?: boolean
  children?: ReactNode
}): ReactElement | null => {
  // No anatomy for exams the map cannot draw — the generic rail stays the
  // navigation surface
  if (!aptmLayoutFits(recordings)) {
    return null
  }

  return (
    <div className="aptm-preview" aria-label="APTM Exam Overview" data-testid="exam-overview">
      <div className="aptm-preview-head">
        <span className="aptm-preview-title">Exam Overview</span>
      </div>
      <div className="aptm-map" aria-label="Cardiac auscultation areas">
        <img className="aptm-map-img" src={MAP_IMAGE_URL} alt="" aria-hidden="true" />
        {Object.values(
          recordings.reduce<{
            [code: string]: { code: HeartSiteCode; label: string; group: PcgReviewRecordingBundle[] }
          }>((groups, recording) => {
            const site = recording.site
            if (!site || !isHeartSiteCode(site.code)) {
              return groups
            }
            if (!groups[site.code]) {
              groups[site.code] = { code: site.code, label: site.label, group: [] }
            }
            groups[site.code].group.push(recording)
            return groups
          }, {})
        ).map(({ code, label, group }) => {
          const marker = MARKERS[code]
          const site = { code: marker.letter, label }
          const groupIds = group.map((recording) => recording.auscultationId)
          const selectedIndex = groupIds.indexOf(selectedAuscultationId ?? "")
          const current = selectedIndex >= 0
          const displayIndex = current ? selectedIndex : 0
          const displayed = group[displayIndex]
          // First recording when the site isn't selected; otherwise the next
          // one at this site, wrapping around
          const nextId = groupIds[current ? (selectedIndex + 1) % groupIds.length : 0]
          const evidence = previewEvidence?.recordings?.[displayed.auscultationId]
          const hasTiming = Boolean(evidence?.timing)
          const hasEnergy = Boolean(evidence?.energy ?? evidence?.soi)
          const useful = hasTiming || hasEnergy
          const decision = displayed.disposition?.decision
          const reviewState = reviewStateText(decision, useful)
          const title = `${hoverText(site.label, group.length, hasTiming, hasEnergy)} Status: ${reviewState}.`
          const position = `${displayIndex + 1} of ${group.length}`
          const classes = [
            "aptm-marker",
            current ? "current" : "",
            useful ? "usable" : "limited",
            decision === "accepted" || decision === "rejected" ? decision : "",
          ]
            .filter(Boolean)
            .join(" ")
          return (
            <Tooltip key={site.code} title={tipsEnabled ? title : ""}>
              <button
                type="button"
                className={classes}
                aria-label={`Open ${site.label} recording ${position} · ${reviewState}${current ? " · Current" : ""}`}
                data-aptm-recording={nextId}
                data-aptm-recordings={JSON.stringify(groupIds)}
                data-testid={`aptm-marker-${site.code}`}
                data-current={current || undefined}
                data-usable={useful || undefined}
                onClick={() => onSelect(nextId)}
                style={{ left: `${marker.left}%`, top: `${marker.top}%` }}
              >
                <span>{site.code}</span>
                {group.length > 1 ? (
                  <span className="aptm-count" aria-label={`Recording ${position}`}>
                    {displayIndex + 1}/{group.length}
                  </span>
                ) : null}
              </button>
            </Tooltip>
          )
        })}
      </div>
      {children}
    </div>
  )
}

export default ExamOverviewMap
export { hoverText, aptmLayoutFits }
